import { notify } from '@onsaui'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { generalApi } from '@/modules/general'
import { icpActions } from '@/modules/icp'
import { icpApi } from '@/modules/icp/api'
import { IcpStage } from '@/modules/icp/model'
import getErrorMessage from '@/modules/shared/helpers/getErrorMessage'
import AnalyticsService from '@/services/analytics'

const useIcpActions = () => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isFileAdding, setIsFileAdding] = useState(false)
  const [isFileDeleting, setIsFileDeleting] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)

  const { workspaceId, icpId: paramIcpId } = useParams()
  const navigate = useNavigate()

  const updateIcp = async (
    icpId: string,
    data: Partial<{ context: string; body: string; stage: IcpStage }>,
    config?: { silent?: boolean },
  ) => {
    AnalyticsService.trackEvent('IcpActions.UpdateIcp.Start', { icpId })
    setIsUpdating(true)
    try {
      const updatedIcp = await icpActions.updateIcp(icpId, data)
      if (!config?.silent) {
        notify({ message: 'Saved', variant: 'success' })
      }

      AnalyticsService.trackEvent('IcpActions.UpdateIcp.Success', { icpId })

      return updatedIcp
    } catch (e) {
      notify({ message: getErrorMessage(e), variant: 'error' })
      AnalyticsService.trackEvent('IcpActions.UpdateIcp.Error', {
        icpId,
        message: getErrorMessage(e),
      })
      throw e
    } finally {
      setIsUpdating(false)
    }
  }

  const updateIcpStage = async (icpId: string, stage: IcpStage) => {
    AnalyticsService.trackEvent('IcpActions.UpdateIcpStage.Start', { icpId, stage })
    setIsUpdating(true)
    try {
      const updatedIcp = await icpActions.updateIcpStage(icpId, stage)
      AnalyticsService.trackEvent('IcpActions.UpdateIcpStage.Success', { icpId, stage })

      return updatedIcp
    } catch (e) {
      notify({ message: getErrorMessage(e), variant: 'error' })
      AnalyticsService.trackEvent('IcpActions.UpdateIcpStage.Error', {
        icpId,
        stage,
        message: getErrorMessage(e),
      })
      throw e
    } finally {
      setIsUpdating(false)
    }
  }

  const regenerateIcp = async (icpId: string) => {
    AnalyticsService.trackEvent('IcpActions.RegenerateIcp.Start', { icpId })
    setIsRegenerating(true)
    try {
      await icpActions.regenerateIcp(icpId)
      AnalyticsService.trackEvent('IcpActions.RegenerateIcp.Success', { icpId })
    } catch (e) {
      notify({ message: getErrorMessage(e), variant: 'error' })
      AnalyticsService.trackEvent('IcpActions.RegenerateIcp.Error', {
        icpId,
        message: getErrorMessage(e),
      })
      throw e
    } finally {
      setIsRegenerating(false)
    }
  }

  const deleteIcp = async (icpId: string, onDeleted?: () => void) => {
    AnalyticsService.trackEvent('IcpActions.DeleteIcp.Start', { icpId })

    setIsDeleting(true)

    let isDeleted = false
    try {
      await icpActions.deleteIcp(icpId)
      AnalyticsService.trackEvent('IcpActions.DeleteIcp.Success', { icpId })

      if (paramIcpId === icpId) {
        if (workspaceId) {
          navigate(`/${workspaceId}`)
        } else {
          navigate('/')
        }
      }

      isDeleted = true
    } catch (e) {
      notify({ message: getErrorMessage(e), variant: 'error' })
      AnalyticsService.trackEvent('IcpActions.DeleteIcp.Error', {
        icpId,
        message: getErrorMessage(e),
      })
      throw e
    } finally {
      setIsDeleting(false)
    }

    if (isDeleted) {
      onDeleted?.()
    }
  }

  const addFile = async (icpId: string, file: File, onProgress?: (progress: number) => void) => {
    AnalyticsService.trackEvent('IcpActions.AddFile.Start', { icpId })

    onProgress?.(0)
    setIsFileAdding(true)
    try {
      const fileConfig = {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      }

      const signedUrl = await icpApi.createSignedUrl(icpId, fileConfig)

      await generalApi.uploadFileToBucket(signedUrl, file, (progress) => {
        onProgress?.(progress)
      })
      onProgress?.(1)

      await icpActions.addFile(icpId, fileConfig)

      AnalyticsService.trackEvent('IcpActions.AddFile.Success', { icpId })
    } catch (e) {
      notify({ message: getErrorMessage(e), variant: 'error' })
      AnalyticsService.trackEvent('IcpActions.AddFile.Error', {
        icpId,
        message: getErrorMessage(e),
      })
      throw e
    } finally {
      setIsFileAdding(false)
    }
  }

  const deleteFile = async (icpId: string, fileName: string) => {
    AnalyticsService.trackEvent('IcpActions.DeleteFile.Start', { icpId, fileName })
    setIsFileDeleting(true)
    try {
      await icpActions.deleteFile(icpId, fileName)
      AnalyticsService.trackEvent('IcpActions.DeleteFile.Success', { icpId, fileName })
    } catch (e) {
      notify({ message: getErrorMessage(e), variant: 'error' })
      AnalyticsService.trackEvent('IcpActions.DeleteFile.Error', {
        icpId,
        fileName,
        message: getErrorMessage(e),
      })
      throw e
    } finally {
      setIsFileDeleting(false)
    }
  }

  return {
    deleteIcp,
    isDeleting,
    updateIcp,
    updateIcpStage,
    isUpdating,
    deleteFile,
    isFileDeleting,
    addFile,
    isFileAdding,
    regenerateIcp,
    isRegenerating,
  }
}

export default useIcpActions
